import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { ClientSpla } from '../types/models/Spla';
import type { ApiResponseClientSpla } from '../types/api/ApiResponseSpla';

interface UseClientSplaReturn {
  spla: ClientSpla | null;
  loading: boolean;
  /** Human-readable error message (the backend's JSend `message`), or null. */
  error: string | null;
  refetch: () => Promise<void>;
}

// GET /clients/spla — the in-scope organization's CURRENT Microsoft SPLA consumption and the sale
// price it pays for it (tenant read; the useClientSpecials precedent). One line per consumed
// license with a per-subject drill-down; the distributor cost, staff notes, and provenance are
// never in this payload. useApiFetch injects the impersonation header, so the read follows the
// impersonated client with no extra work here.
//
// `enabled` (default true) defers the fetch — the Licenses tab mounts lazily via mountOnEnter, so
// in practice this fires on first tab open.
export const useClientSpla = (enabled = true): UseClientSplaReturn => {
  const [spla, setSpla] = useState<ClientSpla | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchSpla = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/clients/spla');
      // Branch on the JSend `body.status`, NEVER `response.ok`: the backend maps controller
      // exceptions to `status: 'error'` at HTTP 200, so `response.ok` can be true on a failure.
      const body = (await response.json().catch(() => null)) as ApiResponseClientSpla | null;
      if (body?.status === 'success' && body.data?.spla) {
        setSpla(body.data.spla);
      } else {
        setError(body?.message ?? 'Failed to load the SPLA consumption');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching the SPLA consumption');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      void fetchSpla();
    }
  }, [enabled, auth.isAuthenticated, fetchSpla]);

  return { spla, loading, error, refetch: fetchSpla };
};
